
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Eye, EyeOff, Bot, Zap, CheckCircle, Trash2 } from "lucide-react";
import { saveProvider } from "@/utils/emailUtils";
import { EmailProvider } from "@/types/email";
import { saveAPIKey, clearAPIKey } from "@/utils/ai/apiKeyUtils";

interface AIProvidersSetupProps {
  openaiProvider?: EmailProvider;
  perplexityProvider?: EmailProvider;
  onUpdate: () => void;
}

const AIProvidersSetup = ({ 
  openaiProvider, 
  perplexityProvider, 
  onUpdate 
}: AIProvidersSetupProps) => {
  const [openaiKey, setOpenaiKey] = useState("");
  const [perplexityKey, setPerplexityKey] = useState("");
  const [showOpenaiKey, setShowOpenaiKey] = useState(false);
  const [showPerplexityKey, setShowPerplexityKey] = useState(false);

  useEffect(() => {
    setOpenaiKey(openaiProvider?.apiKey || "");
    setPerplexityKey(perplexityProvider?.apiKey || "");
  }, [openaiProvider, perplexityProvider]);

  const handleSaveOpenAI = () => {
    if (!openaiKey.trim()) {
      toast.error("Please enter your OpenAI API key");
      return;
    }

    if (!openaiKey.startsWith('sk-')) {
      toast.error("OpenAI API key should start with 'sk-'");
      return;
    }

    const provider: EmailProvider = {
      id: "openai",
      name: "openai",
      displayName: "OpenAI",
      apiKey: openaiKey,
      token: "", // Empty token for AI providers
      isConfigured: true
    };

    saveProvider(provider);
    // Also save in the format expected by apiKeyUtils for enrichment operations
    saveAPIKey("openai", openaiKey);
    toast.success("OpenAI API key saved successfully");
    onUpdate();
  };

  const handleSavePerplexity = () => {
    if (!perplexityKey.trim()) {
      toast.error("Please enter your Perplexity API key");
      return;
    }

    if (!perplexityKey.startsWith('pplx-')) {
      toast.error("Perplexity API key should start with 'pplx-'");
      return;
    }

    const provider: EmailProvider = {
      id: "perplexity",
      name: "perplexity",
      displayName: "Perplexity",
      apiKey: perplexityKey,
      token: "", // Empty token for AI providers
      isConfigured: true
    };

    saveProvider(provider);
    // Also save in the format expected by apiKeyUtils for enrichment operations
    saveAPIKey("perplexity", perplexityKey);
    toast.success("Perplexity API key saved successfully");
    onUpdate();
  };

  const handleRemoveProvider = (providerName: string) => {
    localStorage.removeItem(`${providerName}_provider`);
    // Clear all possible API key storage formats
    clearAPIKey(providerName as 'openai' | 'perplexity');
    
    if (providerName === "openai") {
      setOpenaiKey("");
    } else if (providerName === "perplexity") {
      setPerplexityKey("");
    }
    
    toast.success(`${providerName === "openai" ? "OpenAI" : "Perplexity"} provider removed`);
    onUpdate();
  };

  return (
    <div className="space-y-6">
      {/* OpenAI Provider */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-green-600" />
            OpenAI
            {openaiProvider?.isConfigured && (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Configured
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Used for AI-powered email generation and investor analysis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="openai-key">API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="openai-key"
                  type={showOpenaiKey ? "text" : "password"}
                  placeholder="sk-..."
                  value={openaiKey}
                  onChange={(e) => setOpenaiKey(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2"
                  onClick={() => setShowOpenaiKey(!showOpenaiKey)}
                >
                  {showOpenaiKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button onClick={handleSaveOpenAI}>Save</Button>
              {openaiProvider?.isConfigured && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleRemoveProvider("openai")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Get your API key from{" "}
            <a
              href="https://platform.openai.com/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              OpenAI Platform
            </a>
          </p>
        </CardContent>
      </Card>

      {/* Perplexity Provider */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-600" />
            Perplexity
            {perplexityProvider?.isConfigured && (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Configured
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Used for real-time web research and investor enrichment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="perplexity-key">API Key</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  id="perplexity-key"
                  type={showPerplexityKey ? "text" : "password"}
                  placeholder="pplx-..."
                  value={perplexityKey}
                  onChange={(e) => setPerplexityKey(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2"
                  onClick={() => setShowPerplexityKey(!showPerplexityKey)}
                >
                  {showPerplexityKey ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <Button onClick={handleSavePerplexity}>Save</Button>
              {perplexityProvider?.isConfigured && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleRemoveProvider("perplexity")}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Get your API key from{" "}
            <a
              href="https://www.perplexity.ai/settings/api"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Perplexity API
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIProvidersSetup;
